
import { createFileRoute, type SearchSchemaInput } from '@tanstack/react-router';
import { useGetTrainingAttempt } from '../hooks/trainingAttempts/useTrainingAttempts';
import type { ApiTrainingAttempt } from '../api/trainingAttempts.api';
import type { ApiAttempt } from '../api/simulations.api';
import { CollectionType, type Collection } from '@/types/collection';
import { trainingAttemptSession } from '../store/trainingAttemptSession';
import SimulationCorrection from '../components/SimulationFlow/SimulationCorrection';
import Loader from '../components/Loader/Loader';
import GeneralError from '../components/Errors/General';

export const Route = createFileRoute('/esercitazioni/$id_/correzione')({
  validateSearch: (search: { from?: string } & SearchSchemaInput): { from: string } => ({
    from: search.from ?? 'esercitazioni',
  }),
  component: RouteComponent,
});

function adaptToAttempt(ta: ApiTrainingAttempt): ApiAttempt {
  return {
    _id: ta._id,
    collectionId: ta.poolId,
    brandId: ta.brandId,
    status: ta.status,
    attemptNum: 1,
    time: { total: 0, remaining: 0, startTime: ta.createdAt, endTime: ta.createdAt },
    questions: ta.questions.map((q) => ({
      attemptItemId: q.questionId,
      questionId: q.questionId,
      type: q.type,
      alternativeGiven: q.alternativeGiven ?? null,
      completionAnswerGiven: q.completionAnswerGiven ?? null,
      answerResult: q.answerResult ?? null,
      question: {
        _id: q.questionId,
        type: q.question.type,
        questionText: q.question.questionText,
        questionImages: q.question.questionImages,
        alternatives: q.question.alternatives,
        completionAnswers: q.question.completionAnswers,
        subject: q.question.subject,
        topic: q.question.topic,
        difficulty: q.question.difficulty,
        explanationText: q.question.explanationText,
        explanationImages: q.question.explanationImages,
      },
    })),
  };
}

function makeStubCollection(name: string): Collection {
  return {
    _id: '',
    type: CollectionType.EXERCISE,
    name,
    attributes: {},
    status: 'active',
    archived: false,
    timed: false,
    createdAt: '',
    updatedAt: '',
  };
}

function RouteComponent() {
  const { id } = Route.useParams();
  const { from } = Route.useSearch();
  const backPath = `/${from}`;

  const sessionAttempt = trainingAttemptSession.get();
  const hasSession = sessionAttempt?._id === id;

  const { attempt: fetchedAttempt, isFetching, isError } = useGetTrainingAttempt(hasSession ? '' : id);
  const trainingAttempt = hasSession ? sessionAttempt : fetchedAttempt;

  if (!hasSession && isFetching && !trainingAttempt) return <Loader fullScreen />;
  if (isError || !trainingAttempt) return <GeneralError />;

  return (
    <SimulationCorrection
      attempt={adaptToAttempt(trainingAttempt)}
      collection={makeStubCollection(trainingAttempt.name)}
      backPath={backPath}
    />
  );
}
